import type { Item } from "@egalregal/shared";
import { requests } from "../../shared/api";
import { UnreachableError } from "../../shared/api/errors";
import {
  getMirroredPlacements,
  recordMirroredQuantity,
  removeMirroredPlacement,
} from "../search/catalogStore";

/**
 * The one placement write path (placement-ledger 02).
 *
 * Every surface that sets, adds or removes a placement goes through this
 * module: it owns the server call, the browser mirror, and the failure
 * vocabulary. `setTotal` is the absolute PATCH, `add` the additive POST (the
 * server merges) and `remove` the DELETE — the server asymmetry is exposed,
 * not re-derived (spec decision 4). A write that never reaches the server is
 * `unreachable` (ADR-0005); one the server answers with an error is `refused`.
 */

/** A placement identified across the slice: `containerId:itemId`. */
export type PlacementKey = `${string}:${string}`;

/** What one placement write did, as every surface sees it. */
export type PlacementOutcome =
  | { status: "saved"; quantity: number }
  | { status: "unreachable" }
  | { status: "refused"; error: unknown };

/**
 * The one-level undo slot's payload. `previous` is the total before the saved
 * write; 0 means the placement did not exist. The item travels with it so undo
 * can restore a removal without trusting a later mirror lookup.
 */
export interface PlacementUndo {
  key: PlacementKey;
  previous: number;
  item: Item;
}

/**
 * The slice/ledger key for one placement.
 *
 * @param containerId the holding container.
 * @param itemId the placed item.
 * @returns the `containerId:itemId` key; the search mirror keys its records the
 *   other way round (`itemId:containerId`).
 */
export function placementKey(containerId: string, itemId: string): PlacementKey {
  return `${containerId}:${itemId}`;
}

/**
 * Write one placement's absolute total. Zero means "remove" and never becomes
 * a PATCH of 0 (spec decision 4).
 */
export async function setTotal(
  containerId: string,
  item: Item,
  quantity: number,
): Promise<PlacementOutcome> {
  if (quantity <= 0) return remove(containerId, item);
  try {
    const saved = await requests.items.setQuantity(containerId, item.id, quantity);
    recordMirroredQuantity(containerId, saved.item, saved.quantity);
    return { status: "saved", quantity: saved.quantity };
  } catch (err) {
    return failed(err);
  }
}

/**
 * Add `amount` to one placement through the server's additive POST: it creates
 * the placement or merges into the existing one, and answers with the new total.
 */
export async function add(
  containerId: string,
  item: Item,
  amount: number,
): Promise<PlacementOutcome> {
  return addBarcode(containerId, item.barcode, amount);
}

/**
 * Add `amount` copies of a barcode through the container-scoped POST.
 *
 * This is the one add path for a code whose item may not exist on the client
 * yet: the server resolves (or creates) the item, merges the placement and, on
 * a first-time barcode, keeps its provider work (including the local photo
 * download). The browser mirror is written from the item the server returns,
 * so the client never has to create the item first. A known item's `add`
 * delegates here; the only difference is that the caller already holds the
 * item for its undo entry.
 *
 * @param containerId the holding container.
 * @param barcode the item's barcode (also how an unknown item is created).
 * @param amount how many copies to add; the server merges.
 * @returns the saved total, or the same unreachable/refused split as every
 *   other ledger write.
 */
export async function addBarcode(
  containerId: string,
  barcode: string,
  amount: number,
): Promise<PlacementOutcome> {
  try {
    const saved = await requests.items.add(containerId, barcode, amount);
    recordMirroredQuantity(containerId, saved.item, saved.quantity);
    return { status: "saved", quantity: saved.quantity };
  } catch (err) {
    return failed(err);
  }
}

/** Remove one placement; the item itself stays. */
export async function remove(
  containerId: string,
  item: Item,
): Promise<PlacementOutcome> {
  try {
    await requests.items.remove(containerId, item.id);
    removeMirroredPlacement(item.id, containerId);
    return { status: "saved", quantity: 0 };
  } catch (err) {
    return failed(err);
  }
}

/**
 * Restore the one undo slot's exact previous state (spec decision 6).
 *
 * The mirror is the arbiter of whether the placement still exists: a removed
 * placement is restored with an additive POST at `previous` (never at 1),
 * while a placement whose total changed is PATCHed back. An `add` that created
 * a placement (previous 0) is undone by removing it. There is one slot, so a
 * missing entry is a no-op.
 */
export async function undoLast(undo: PlacementUndo | null): Promise<PlacementOutcome> {
  if (!undo) return { status: "saved", quantity: 0 };

  const { containerId, itemId } = splitPlacementKey(undo.key);
  const exists = getMirroredPlacements().some(
    (placement) => placement.itemId === itemId && placement.containerId === containerId,
  );

  if (!exists) {
    // Nothing to restore when the previous state was also "no placement".
    if (undo.previous <= 0) return { status: "saved", quantity: 0 };
    return add(containerId, undo.item, undo.previous);
  }
  if (undo.previous <= 0) return remove(containerId, undo.item);
  return setTotal(containerId, undo.item, undo.previous);
}

/** Split a placement key back into its container and item ids. */
function splitPlacementKey(key: PlacementKey): { containerId: string; itemId: string } {
  const separator = key.indexOf(":");
  return {
    containerId: key.slice(0, separator),
    itemId: key.slice(separator + 1),
  };
}

/** `unreachable` only when nobody answered; every other failure is a refusal. */
function failed(err: unknown): PlacementOutcome {
  return err instanceof UnreachableError
    ? { status: "unreachable" }
    : { status: "refused", error: err };
}
